import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminService, profileService } from '../../services/api';
import '../../styles/Admin.css';

// Fonction utilitaire pour mettre à jour les profils après modification d'une expertise
const updateProfilesExpertise = async (oldExpertise, newExpertise) => {
  try {
    // Récupérer tous les profils (y compris ceux qui ne sont pas approuvés)
    const profiles = await adminService.getAllProfiles();
    
    // Filtrer les profils qui contiennent l'ancienne expertise
    const profilesToUpdate = profiles.filter(profile => 
      profile.expertise && profile.expertise.includes(oldExpertise)
    );
    
    console.log(`${profilesToUpdate.length} profils à mettre à jour pour l'expertise: ${oldExpertise} -> ${newExpertise}`);
    
    // Compter les profils par statut
    const approvedCount = profilesToUpdate.filter(p => p.status === 'approved').length;
    const pendingCount = profilesToUpdate.filter(p => p.status === 'pending').length;
    const rejectedCount = profilesToUpdate.filter(p => p.status === 'rejected').length;
    
    console.log(`Statuts des profils à mettre à jour: ${approvedCount} approuvés, ${pendingCount} en attente, ${rejectedCount} rejetés`);
    
    // Mettre à jour chaque profil
    const updatePromises = profilesToUpdate.map(async profile => {
      // Créer une nouvelle liste d'expertises avec l'ancienne expertise remplacée par la nouvelle
      const updatedExpertises = profile.expertise.map(exp => 
        exp === oldExpertise ? newExpertise : exp
      );
      
      // Mettre à jour le profil avec les nouvelles expertises
      const updatedProfile = {
        ...profile,
        expertise: updatedExpertises
      };
      
      console.log(`Mise à jour du profil ${profile._id} (statut: ${profile.status}) avec les expertises:`, updatedExpertises);
      
      // Envoyer la mise à jour au serveur
      return await profileService.updateProfile(profile._id, updatedProfile);
    });
    
    // Attendre que toutes les mises à jour soient terminées
    await Promise.all(updatePromises);
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des profils:', error);
    return false;
  }
};

// Fonction utilitaire pour supprimer une expertise de tous les profils
const removeExpertiseFromProfiles = async (expertiseToRemove) => {
  try {
    // Récupérer tous les profils (y compris ceux qui ne sont pas approuvés)
    const profiles = await adminService.getAllProfiles();
    
    // Filtrer les profils qui contiennent l'expertise à supprimer
    const profilesToUpdate = profiles.filter(profile => 
      profile.expertise && profile.expertise.includes(expertiseToRemove)
    );
    
    console.log(`${profilesToUpdate.length} profils à mettre à jour pour supprimer l'expertise: ${expertiseToRemove}`);
    
    // Compter les profils par statut
    const approvedCount = profilesToUpdate.filter(p => p.status === 'approved').length;
    const pendingCount = profilesToUpdate.filter(p => p.status === 'pending').length;
    const rejectedCount = profilesToUpdate.filter(p => p.status === 'rejected').length;
    
    console.log(`Statuts des profils à mettre à jour: ${approvedCount} approuvés, ${pendingCount} en attente, ${rejectedCount} rejetés`);
    
    // Mettre à jour chaque profil
    const updatePromises = profilesToUpdate.map(async profile => {
      // Créer une nouvelle liste d'expertises sans l'expertise à supprimer
      const updatedExpertises = profile.expertise.filter(exp => exp !== expertiseToRemove);
      
      // S'assurer qu'il reste au moins une expertise (obligatoire dans le modèle)
      if (updatedExpertises.length === 0) {
        // Ne rien ajouter si aucune expertise n'est sélectionnée
      }
      
      // Mettre à jour le profil avec les nouvelles expertises
      const updatedProfile = {
        ...profile,
        expertise: updatedExpertises
      };
      
      console.log(`Mise à jour du profil ${profile._id} (statut: ${profile.status}) avec les expertises:`, updatedExpertises);
      
      // Envoyer la mise à jour au serveur
      return await profileService.updateProfile(profile._id, updatedProfile);
    });
    
    // Attendre que toutes les mises à jour soient terminées
    await Promise.all(updatePromises);
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'expertise des profils:', error);
    return false;
  }
};

const ExpertiseManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expertises, setExpertises] = useState([]);
  const [newExpertise, setNewExpertise] = useState('');
  const [editingExpertise, setEditingExpertise] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    if (!currentUser || !currentUser.isAdmin) {
      navigate('/');
    }
  }, [currentUser, navigate]);
  
  // Charger toutes les expertises
  useEffect(() => {
    const fetchExpertises = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Récupérer tous les profils, y compris ceux qui ne sont pas approuvés
        const profiles = await adminService.getAllProfiles();
        
        // Extraire toutes les expertises de tous les profils
        const allExpertises = profiles.flatMap(profile => profile.expertise || []);
        
        // Créer des ensembles d'expertises par statut de profil
        const approvedExpertiseSet = new Set(
          profiles
            .filter(profile => profile.status === 'approved')
            .flatMap(profile => profile.expertise || [])
        );
        
        const pendingExpertiseSet = new Set(
          profiles
            .filter(profile => profile.status === 'pending')
            .flatMap(profile => profile.expertise || [])
        );
        
        const rejectedExpertiseSet = new Set(
          profiles
            .filter(profile => profile.status === 'rejected')
            .flatMap(profile => profile.expertise || [])
        );
        
        // Créer un tableau d'objets d'expertise avec des métadonnées sur leur statut
        const expertiseObjects = [...new Set(allExpertises)].map(expertise => ({
          name: expertise,
          inApprovedProfile: approvedExpertiseSet.has(expertise),
          inPendingProfile: pendingExpertiseSet.has(expertise),
          inRejectedProfile: rejectedExpertiseSet.has(expertise),
          // Compter le nombre de profils qui utilisent cette expertise
          usageCount: profiles.filter(profile => 
            profile.expertise && profile.expertise.includes(expertise)
          ).length
        }));
        
        // Trier par ordre alphabétique
        expertiseObjects.sort((a, b) => a.name.localeCompare(b.name));
        
        console.log(`${expertiseObjects.length} expertises uniques extraites de ${profiles.length} profils (tous statuts confondus)`);
        console.log(`Expertises de profils approuvés: ${approvedExpertiseSet.size}`);
        console.log(`Expertises de profils en attente: ${pendingExpertiseSet.size}`);
        console.log(`Expertises de profils rejetés: ${rejectedExpertiseSet.size}`);
        
        setExpertises(expertiseObjects);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des expertises:', err);
        setError('Impossible de charger les expertises. Veuillez réessayer plus tard.');
        setLoading(false);
      }
    };
    
    fetchExpertises();
  }, []);
  
  // Ajouter une nouvelle expertise
  const handleAddExpertise = () => {
    if (newExpertise.trim() === '') return;
    
    // Vérifier si l'expertise existe déjà
    if (expertises.some(exp => exp.name === newExpertise.trim())) {
      setError('Cette expertise existe déjà.');
      return;
    }
    
    // Ajouter la nouvelle expertise (pas besoin de mettre à jour les profils car aucun ne l'utilise encore)
    setExpertises([...expertises, { name: newExpertise.trim(), inApprovedProfile: false, inPendingProfile: false, inRejectedProfile: false, usageCount: 0 }].sort((a, b) => a.name.localeCompare(b.name)));
    setNewExpertise('');
    setError(null);
  };
  
  // Commencer l'édition d'une expertise
  const startEditing = (expertise) => {
    setEditingExpertise(expertise.name);
    setEditValue(expertise.name);
  };
  
  // Enregistrer les modifications d'une expertise
  const saveEdit = async () => {
    if (editValue.trim() === '') return;
    
    // Vérifier si l'expertise existe déjà
    if (expertises.some(exp => exp.name === editValue.trim()) && editValue.trim() !== editingExpertise) {
      setError(`L'expertise "${editValue}" existe déjà.`);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Mettre à jour tous les profils qui utilisent cette expertise
      const updateSuccess = await updateProfilesExpertise(editingExpertise, editValue);
      
      if (updateSuccess) {
        // Mettre à jour la liste des expertises
        setExpertises(expertises.map(exp => {
          if (exp.name === editingExpertise) {
            return { ...exp, name: editValue };
          }
          return exp;
        }));
        setEditingExpertise(null);
        setEditValue('');
      } else {
        setError('Erreur lors de la mise à jour des profils. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Erreur lors de la modification de l\'expertise:', err);
      setError('Impossible de modifier l\'expertise. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };
  
  // Annuler l'édition
  const cancelEdit = () => {
    setEditingExpertise(null);
    setEditValue('');
  };
  
  // Supprimer une expertise
  const handleDeleteExpertise = async (expertise) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'expertise "${expertise.name}" ?`)) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Supprimer l'expertise de tous les profils
      const removeSuccess = await removeExpertiseFromProfiles(expertise.name);
      
      if (removeSuccess) {
        // Mettre à jour la liste des expertises
        setExpertises(expertises.filter(exp => exp.name !== expertise.name));
      } else {
        setError('Erreur lors de la suppression de l\'expertise des profils. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'expertise:', err);
      setError('Impossible de supprimer l\'expertise. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="expertise-management">
      <div className="container">
        <h1 className="page-title">Gestion des expertises</h1>
        
        <div className="admin-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/admin')}
          >
            <i className="fas fa-arrow-left"></i> Retour au tableau de bord
          </button>
        </div>
        
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}
        
        <div className="add-expertise-form">
          <h2>Ajouter une expertise</h2>
          <div className="form-row">
            <input
              type="text"
              value={newExpertise}
              onChange={(e) => setNewExpertise(e.target.value)}
              placeholder="Nouvelle expertise"
              className="form-control"
            />
            <button
              onClick={handleAddExpertise}
              className="btn btn-primary"
              disabled={newExpertise.trim() === ''}
            >
              <i className="fas fa-plus"></i> Ajouter
            </button>
          </div>
        </div>
        
        <div className="expertise-list">
          <h2>Liste des expertises</h2>
          
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Chargement des expertises...</p>
            </div>
          ) : expertises.length === 0 ? (
            <div className="empty-message">
              <p>Aucune expertise trouvée.</p>
            </div>
          ) : (
            <div className="expertise-table">
              <table>
                <thead>
                  <tr>
                    <th>Expertise</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <caption className="table-info">
                  Les expertises provenant de profils non approuvés sont identifiées par des badges de statut.
                  Vous pouvez les modifier avant d'approuver les profils correspondants.
                </caption>
                <tbody>
                  {expertises.map((expertise, index) => (
                    <tr key={index} className={`expertise-row ${!expertise.inApprovedProfile ? 'pending-expertise' : ''}`}>
                      <td>
                        {editingExpertise === expertise.name ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="form-control"
                            autoFocus
                          />
                        ) : (
                          <div className="expertise-info">
                            <span className="expertise-name">{expertise.name}</span>
                            <div className="expertise-badges">
                              {expertise.inApprovedProfile && (
                                <span className="status-badge approved" title="Utilisée dans des profils approuvés">Approuvé</span>
                              )}
                              {expertise.inPendingProfile && (
                                <span className="status-badge pending" title="Utilisée dans des profils en attente">En attente</span>
                              )}
                              {expertise.inRejectedProfile && (
                                <span className="status-badge rejected" title="Utilisée dans des profils rejetés">Rejeté</span>
                              )}
                              <span className="usage-count" title="Nombre de profils utilisant cette expertise">
                                {expertise.usageCount} {expertise.usageCount > 1 ? 'profils' : 'profil'}
                              </span>
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        {editingExpertise === expertise.name ? (
                          <div className="action-buttons">
                            <button
                              onClick={saveEdit}
                              className="btn btn-success btn-sm"
                              disabled={editValue.trim() === ''}
                            >
                              <i className="fas fa-check"></i> Enregistrer
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="btn btn-secondary btn-sm"
                            >
                              <i className="fas fa-times"></i> Annuler
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              onClick={() => startEditing(expertise)}
                              className="btn btn-primary btn-sm"
                            >
                              <i className="fas fa-edit"></i> Modifier
                            </button>
                            <button
                              onClick={() => handleDeleteExpertise(expertise)}
                              className="btn btn-danger btn-sm"
                            >
                              <i className="fas fa-trash"></i> Supprimer
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertiseManagement;
